import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Plus, 
  Trash2, 
  X,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface FinancialRecord {
  id: string;
  type: 'income' | 'expense';
  category: string;
  amount: number;
  description: string;
  date: string;
}

export function AdminFinancial({ orders }: { orders: any[] }) {
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddRecordOpen, setIsAddRecordOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({
    type: 'expense' as 'income' | 'expense',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const fetchRecords = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('financial_records')
      .select('*')
      .order('date', { ascending: false });
    
    if (!error) setRecords((data || []) as any[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRecord.amount || !newRecord.category) return;

    const { error } = await supabase
      .from('financial_records')
      .insert([{
        ...newRecord,
        amount: parseFloat(newRecord.amount)
      }]);

    if (!error) {
      toast.success("Registro adicionado!");
      setIsAddRecordOpen(false);
      setNewRecord({
        type: 'expense',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchRecords();
    } else {
      toast.error("Erro ao salvar registro");
    }
  };

  const deleteRecord = async (id: string) => {
    const { error } = await supabase.from('financial_records').delete().eq('id', id);
    if (!error) fetchRecords();
  };

  const groupedByMonth = records.reduce((acc: any, curr) => {
    const month = new Date(curr.date).toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
    if (!acc[month]) acc[month] = { income: 0, expense: 0, profit: 0 };
    if (curr.type === 'income') acc[month].income += curr.amount;
    else acc[month].expense += curr.amount;
    return acc;
  }, {});

  orders.forEach(order => {
    const month = new Date(order.created_at).toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
    if (!groupedByMonth[month]) groupedByMonth[month] = { income: 0, expense: 0, profit: 0 };
    
    if (order.status !== 'cancelled') {
      groupedByMonth[month].income += order.total_price;
      groupedByMonth[month].expense += (order.production_cost || 0);
    }
  });

  Object.keys(groupedByMonth).forEach(month => {
    groupedByMonth[month].profit = groupedByMonth[month].income - groupedByMonth[month].expense;
  });

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-pink-500" />
          Financeiro
        </h2>
        <Button 
          onClick={() => setIsAddRecordOpen(!isAddRecordOpen)}
          className="bg-pink-500 hover:bg-pink-600 rounded-xl"
        >
          {isAddRecordOpen ? "Cancelar" : "Novo Lançamento"}
        </Button>
      </div>

      {isAddRecordOpen && (
        <Card className="border-pink-100 shadow-sm animate-in slide-in-from-top duration-300">
          <CardHeader>
            <CardTitle className="text-sm font-bold">Adicionar Movimentação</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddRecord} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-gray-400 uppercase">Tipo</Label>
                <Select 
                  value={newRecord.type} 
                  onValueChange={(val: 'income' | 'expense') => setNewRecord({...newRecord, type: val})}
                >
                  <SelectTrigger className="rounded-xl h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Entrada (Caixa/Venda)</SelectItem>
                    <SelectItem value="expense">Saída (Gasto/Despesa)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-gray-400 uppercase">Categoria</Label>
                <Input 
                  placeholder="Ex: Material, Aluguel, Venda"
                  value={newRecord.category}
                  onChange={e => setNewRecord({...newRecord, category: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-gray-400 uppercase">Valor (R$)</Label>
                <Input 
                  type="number"
                  placeholder="0.00"
                  value={newRecord.amount}
                  onChange={e => setNewRecord({...newRecord, amount: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-gray-400 uppercase">Data</Label>
                <Input 
                  type="date"
                  value={newRecord.date}
                  onChange={e => setNewRecord({...newRecord, date: e.target.value})}
                  className="rounded-xl h-10"
                />
              </div>
              <div className="space-y-1 flex items-end">
                <Button type="submit" className="w-full h-10 rounded-xl bg-pink-500 hover:bg-pink-600">
                  Lançar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-none shadow-sm overflow-hidden">
          <CardHeader className="bg-gray-50/50">
            <CardTitle className="text-sm font-bold">Resumo Mensal</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {Object.keys(groupedByMonth).length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">Nenhum registro encontrado.</p>
              ) : (
                Object.entries(groupedByMonth).map(([month, data]: [string, any]) => (
                  <div key={month} className="p-4 rounded-2xl border border-gray-100 space-y-3">
                    <h3 className="font-bold text-gray-900 capitalize">{month}</h3>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase">Entradas</p>
                        <p className="text-sm font-bold text-green-600">{formatCurrency(data.income)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase">Saídas</p>
                        <p className="text-sm font-bold text-red-500">{formatCurrency(data.expense)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase">Saldo</p>
                        <p className={`text-sm font-black ${data.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(data.profit)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm overflow-hidden">
          <CardHeader className="bg-gray-50/50">
            <CardTitle className="text-sm font-bold">Últimos Lançamentos</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {records.slice(0, 10).map(record => (
                <div key={record.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${record.type === 'income' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                      {record.type === 'income' ? <Plus className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">{record.category}</p>
                      <p className="text-[9px] text-gray-400">{new Date(record.date).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className={`text-sm font-black ${record.type === 'income' ? 'text-green-600' : 'text-red-500'}`}>
                      {record.type === 'income' ? '+' : '-'} {formatCurrency(record.amount)}
                    </p>
                    <button onClick={() => deleteRecord(record.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
